import { useRef, useState, useCallback, useEffect } from 'react';

interface RecorderState {
  isRecording: boolean;
  // На паузе (пользователь нажал Pause или вкладка ушла в фон).
  // isRecording при этом остаётся true — запись идёт, просто приостановлена.
  isPaused: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  error: string | null;
  // Громкость микрофона 0..1, обновляется ~30 раз в секунду.
  // Используется для визуализации в UI (amplitude bars).
  volume: number;
  // История сэмплов громкости для waveform. Массив длиной ~60,
  // обновляется вместе с volume. Индекс 0 — самое старое, N-1 — самое новое.
  waveformHistory: number[];
  // Сколько секунд подряд микрофон молчит (volume < порог).
  // 0 если сейчас звук есть. Используется для warning-баннера
  // «микрофон не слышит».
  silenceSeconds: number;
}

// Сколько отсчётов держим в waveform. При ~30 fps это ~2 сек истории —
// смотрится как «живой» скроллинг слева направо.
const WAVEFORM_LENGTH = 60;

// Порог громкости, ниже которого считаем «тихо».
const SILENCE_THRESHOLD = 0.02;

// Как часто (мс) проверяем накопленную тишину для silenceSeconds.
const SILENCE_TICK_MS = 500;

/**
 * Хук для записи аудио через WebAudio API.
 * Запись с микрофона → Blob (webm/opus).
 *
 * Защиты от потерь:
 * - Wake Lock API — экран не уснёт во время записи.
 *   При возврате в видимую вкладку (например, свернули → развернули)
 *   пере-запрашиваем wake lock — браузер отпускает его при потере
 *   видимости и не восстанавливает автоматически.
 * - visibilitychange auto-pause — если вкладка ушла в фон или экран
 *   потух → автоматически ставим на паузу (не стопим). Врач возвращается
 *   и нажимает «Продолжить» — этим избегаем неловких ситуаций типа
 *   «переключился в МИС, а запись всё ещё пишет приватный разговор».
 *   Авто-resume при возврате намеренно НЕТ — только явное подтверждение.
 * - silenceSeconds — метрика «микрофон молчит N сек». UI показывает
 *   баннер если 15+ секунд, чтобы врач мог проверить микрофон.
 */
export function useRecorder(): RecorderState {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setVolume] = useState(0);
  const [waveformHistory, setWaveformHistory] = useState<number[]>(
    () => Array(WAVEFORM_LENGTH).fill(0),
  );
  const [silenceSeconds, setSilenceSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  // Текущая громкость доступна колбэкам через ref без ререндеров.
  const volumeRef = useRef(0);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    if (wakeLockRef.current) return;
    try {
      wakeLockRef.current = await (navigator as unknown as {
        wakeLock: { request(t: 'screen'): Promise<WakeLockSentinel> };
      }).wakeLock.request('screen');
    } catch {
      // Safari iOS часто отклоняет, Firefox только за флагом. Молча.
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch {}
      wakeLockRef.current = null;
    }
  }, []);

  const stopSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setSilenceSeconds(0);
  }, []);

  const startSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) return;
    silenceTimerRef.current = window.setInterval(() => {
      if (volumeRef.current < SILENCE_THRESHOLD) {
        setSilenceSeconds((s) => s + SILENCE_TICK_MS / 1000);
      } else {
        setSilenceSeconds(0);
      }
    }, SILENCE_TICK_MS);
  }, []);

  const stopVolumeMeter = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolume(0);
    volumeRef.current = 0;
    setWaveformHistory(Array(WAVEFORM_LENGTH).fill(0));
    stopSilenceTimer();
  }, [stopSilenceTimer]);

  const startVolumeMeter = useCallback((stream: MediaStream) => {
    try {
      const Ctx: typeof AudioContext =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioCtx = new Ctx();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;

      const buffer = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buffer);
        // RMS амплитуда: ∑((s-128)/128)^2 / N, потом √.
        let sumSq = 0;
        for (let i = 0; i < buffer.length; i++) {
          const v = (buffer[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buffer.length);
        const smoothed = volumeRef.current * 0.6 + Math.min(1, rms * 2.5) * 0.4;
        volumeRef.current = smoothed;
        setVolume(smoothed);
        // Waveform-история: сдвигаем массив, добавляем новый sample.
        setWaveformHistory((prev) => {
          const next = prev.slice(1);
          next.push(smoothed);
          return next;
        });
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      // Отдельный таймер для silenceSeconds — раз в 500 мс проверяем
      // текущую громкость. Если тихо — накапливаем, если громко — сброс.
      startSilenceTimer();
    } catch {
      // Если AudioContext недоступен — UI просто без визуализации.
    }
  }, [startSilenceTimer]);

  const finalize = useCallback((): Blob | null => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return null;
    const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
    recorder.stream.getTracks().forEach((t) => t.stop());
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    releaseWakeLock();
    stopVolumeMeter();
    return blob;
  }, [releaseWakeLock, stopVolumeMeter]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });

      // Safari/iOS не поддерживает webm — пишет в mp4 (AAC).
      // Chrome/Firefox любят webm/opus. Подбираем первый поддерживаемый.
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];
      const mimeType = candidates.find((m) => MediaRecorder.isTypeSupported(m));
      if (!mimeType) {
        throw new Error(
          'Браузер не поддерживает запись аудио. Попробуйте обновить браузер или Chrome/Firefox.',
        );
      }

      const recorder = new MediaRecorder(stream, { mimeType });

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      // Подушка безопасности: timeslice 1 сек — браузер чаще флашит
      // accumulated audio в chunks. Если запись резко прервётся (краш,
      // отзыв permission), потеряем максимум последнюю секунду.
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);

      // Запускаем волюм-метр для UI-визуализации + silence-детектор.
      startVolumeMeter(stream);
      await requestWakeLock();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось получить доступ к микрофону';
      setError(msg);
      throw e;
    }
  }, [requestWakeLock, startVolumeMeter]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder) {
        resolve(null);
        return;
      }
      recorder.onstop = () => resolve(finalize());
      // MediaRecorder.stop() умеет корректно завершать даже paused-запись.
      try { recorder.stop(); } catch { resolve(finalize()); }
    });
  }, [finalize]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    try {
      recorder.pause();
      setIsPaused(true);
      // На паузе silence-таймер не имеет смысла (микрофон отпущен —
      // «тишина» ожидаема). Останавливаем счётчик, чтобы не показывать
      // фейковое «микрофон молчит N сек» после возобновления.
      stopSilenceTimer();
      // Отпускаем wake lock — на паузе экрану можно спать.
      void releaseWakeLock();
    } catch {
      // Некоторые старые Safari не поддерживают pause — молча игнорим.
    }
  }, [releaseWakeLock, stopSilenceTimer]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    try {
      recorder.resume();
      setIsPaused(false);
      // Запускаем silence-таймер заново с нулевым счётчиком.
      startSilenceTimer();
      void requestWakeLock();
    } catch {}
  }, [requestWakeLock, startSilenceTimer]);

  // Авто-пауза при потере видимости (lock screen, переключение вкладки
  // или приложения). Причин две:
  // 1) Android/iOS могут заглушить микрофон в фоне — MediaRecorder
  //    продолжит писать тишину, а мы не заметим.
  // 2) Приватность — если врач переключился в МИС / мессенджер, запись
  //    в фоне пишет всё что говорится рядом с телефоном. Пауза убирает
  //    неловкие ситуации: врач вернётся и увидит явный UI «на паузе».
  //
  // Auto-resume при возврате намеренно НЕТ — только явное действие
  // пользователя. При возврате пере-запрашиваем wake lock (для случая,
  // если пользователь тут же нажмёт Resume).
  useEffect(() => {
    if (!isRecording) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock();
        return;
      }
      pauseRecording();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isRecording, requestWakeLock, pauseRecording]);

  return {
    isRecording,
    isPaused,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    error,
    volume,
    waveformHistory,
    silenceSeconds,
  };
}
